import {
  GaiaEventType,
  WebsocketSubscriptionTarget,
  type GaiaEvent,
  type GaiaEventObjectPayload,
} from '@/api';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { onBeforeUnmount, onMounted, watch } from 'vue';
import { logger } from '@/utils/logger';

export interface UseContractManagementExtractionWebSocketOptions {
  /** Extraction uids to keep subscribed. Reconciled live — subscriptions follow this set. */
  extractionUids: () => string[];
  /** A `contract_management_extraction_update` arrived for a watched extraction — patch it. */
  onExtractionUpdated: (uid: string, data: Record<string, unknown>) => void;
}

function objectPayload(event: GaiaEvent): GaiaEventObjectPayload | null {
  const data = event.data;
  if (typeof data !== 'object' || !('uid' in data) || !('data' in data)) {
    return null;
  }
  return typeof data.uid === 'string' && typeof data.data === 'object' ? data : null;
}

/**
 * Keep contract management extractions in sync with the backend's
 * `contract_management_extraction_update` websocket events. The backend emits
 * one when an extraction finishes, carrying its new `processing_state`,
 * `value`, `error_text`, … — so callers patch the extraction in place rather
 * than refetching.
 *
 * Subscriptions are refcounted in the service, so an overlapping uid across
 * views is safe.
 */
export function useContractManagementExtractionWebSocket(
  options: UseContractManagementExtractionWebSocketOptions
): void {
  const subs = new Map<string, () => void>();
  const disposers: (() => void)[] = [];

  function reconcile(desired: string[]): void {
    const wsClient = getWsClient();
    const desiredSet = new Set(desired.filter(Boolean));

    for (const [uid, dispose] of subs) {
      if (!desiredSet.has(uid)) {
        dispose();
        subs.delete(uid);
      }
    }
    for (const uid of desiredSet) {
      if (!subs.has(uid)) {
        subs.set(
          uid,
          wsClient.addSubscriptionUid(
            WebsocketSubscriptionTarget.CONTRACT_MANAGEMENT_EXTRACTION,
            uid
          )
        );
      }
    }
  }

  const handler: WebSocketEventHandler = event => {
    if (event.gaia_event_type !== GaiaEventType.CONTRACT_MANAGEMENT_EXTRACTION_UPDATE) {
      return;
    }
    const payload = objectPayload(event);
    if (!payload || !subs.has(payload.uid)) {
      return;
    }
    options.onExtractionUpdated(payload.uid, payload.data);
  };

  onMounted(() => {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      logger.warn('[WebSocket] WebSocket is disabled via settings');
      return;
    }
    const wsClient = getWsClient();
    disposers.push(
      wsClient.registerHandler(WebsocketSubscriptionTarget.CONTRACT_MANAGEMENT_EXTRACTION, handler),
      watch(
        options.extractionUids,
        (uids: string[]) => {
          reconcile(uids);
        },
        { immediate: true }
      )
    );
  });

  onBeforeUnmount(() => {
    for (const dispose of subs.values()) {
      dispose();
    }
    subs.clear();
    for (const dispose of disposers) {
      dispose();
    }
    disposers.length = 0;
  });
}
